/**
 * CampaignFrequencyChart.tsx
 *
 * Shows frequency (impressions / reach) per campaign per day as separate lines.
 * Uses raw kpiRows which carry the `reach` field from the raw_ads sheet.
 */

import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { branding } from "../../../../config/branding";
import type { MetaLeadGenRow } from "../types";
import { applyFilters } from "../filters";
import type { MetaLeadGenFilters } from "../types";

const C = branding.colors;

const TICK_STYLE = { fontSize: 10, fill: "currentColor", opacity: 0.5 } as const;
const AXIS_PROPS = { axisLine: false, tickLine: false, tick: TICK_STYLE } as const;
const GRID_PROPS = {
  strokeDasharray: "3 3", vertical: false,
  stroke: "currentColor", strokeOpacity: 0.08,
} as const;

const CHART_H = 220;
const MAX_LABEL_LEN = 28;

function shortenCampaign(name: string): string {
  // Remove common suffixes to save space
  const cleaned = name
    .replace(/_FormSubmit_Webinar_\w+/gi, "")
    .replace(/\s*\|\s*/g, " · ")
    .replace(/_/g, " ");
  return cleaned.length > MAX_LABEL_LEN ? cleaned.slice(0, MAX_LABEL_LEN) + "…" : cleaned;
}

function toDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

interface CampaignInfo { id: string; label: string; color: string }

interface ChartRow { date: string; [key: string]: number | string }

function buildChartData(rows: MetaLeadGenRow[]): { data: ChartRow[]; campaigns: CampaignInfo[] } {
  // Group by campaignId + date, sum impressions & reach
  const byKey = new Map<string, { impressions: number; reach: number }>();
  const campaignMeta = new Map<string, string>(); // id → name

  for (const row of rows) {
    if (!row.date || !row.campaignId) continue;
    const reach = row.reach ?? 0;
    if (reach === 0 && row.impressions === 0) continue;

    campaignMeta.set(row.campaignId, row.campaignName || row.campaignId);
    const key = `${row.campaignId}||${row.date}`;
    const existing = byKey.get(key) ?? { impressions: 0, reach: 0 };
    existing.impressions += row.impressions;
    existing.reach       += reach;
    byKey.set(key, existing);
  }

  if (campaignMeta.size === 0) return { data: [], campaigns: [] };

  // Collect all unique sorted dates
  const dateSet = new Set<string>();
  for (const key of byKey.keys()) dateSet.add(key.split("||")[1]);
  const dates = Array.from(dateSet).sort();

  // Build flat rows: { date, campaignId_1: freq, campaignId_2: freq, ... }
  const data: ChartRow[] = dates.map(date => {
    const row: ChartRow = { date: toDisplayDate(date) };
    for (const [campaignId] of campaignMeta) {
      const key = `${campaignId}||${date}`;
      const agg = byKey.get(key);
      if (agg && agg.reach > 0) {
        row[campaignId] = parseFloat((agg.impressions / agg.reach).toFixed(3));
      }
      // leave undefined (null gap) if no data for this campaign that day
    }
    return row;
  });

  // Assign colors from chart palette
  const campaigns: CampaignInfo[] = Array.from(campaignMeta.entries()).map(([id, name], idx) => ({
    id,
    label: shortenCampaign(name),
    color: C.chart[idx % C.chart.length],
  }));

  return { data, campaigns };
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
function FreqTooltip({
  active, payload, label, campaigns,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
  campaigns: CampaignInfo[];
}) {
  if (!active || !payload?.length) return null;
  const labelMap = Object.fromEntries(campaigns.map(c => [c.id, c.label]));
  return (
    <div className="rounded-lg border border-border bg-popover/95 backdrop-blur-sm px-3 py-2 shadow-xl text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-0.5 last:mb-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-muted-foreground truncate max-w-[160px]">{labelMap[p.dataKey] ?? p.dataKey}:</span>
          <span className="font-medium text-foreground ml-auto pl-2">{p.value.toFixed(2)}×</span>
        </div>
      ))}
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────
interface CampaignFrequencyChartProps {
  rows: MetaLeadGenRow[];
  filters: MetaLeadGenFilters;
}

export function CampaignFrequencyChart({ rows, filters }: CampaignFrequencyChartProps) {
  const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const { data, campaigns } = useMemo(() => buildChartData(filtered), [filtered]);

  const interval = Math.max(0, Math.ceil(data.length / 8) - 1);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="mb-4 text-sm font-semibold text-foreground">Frequency by Campaign</p>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
        {campaigns.map(c => (
          <span key={c.id} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-block w-4 h-[2px] rounded-full" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center text-muted-foreground text-xs" style={{ height: CHART_H }}>
          No frequency data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={CHART_H}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="date" {...AXIS_PROPS} dy={6} interval={interval} />
            <YAxis
              {...AXIS_PROPS}
              tickFormatter={v => `${(v as number).toFixed(1)}×`}
              width={42}
              domain={[0, "auto"]}
            />
            <Tooltip
              content={props => (
                <FreqTooltip
                  active={props.active}
                  payload={props.payload?.map(p => ({
                    dataKey: String(p.dataKey),
                    value: Number(p.value),
                    color: p.color ?? "",
                  }))}
                  label={String(props.label ?? "")}
                  campaigns={campaigns}
                />
              )}
              cursor={{ stroke: "currentColor", strokeOpacity: 0.1 }}
            />
            {campaigns.map(c => (
              <Line
                key={c.id}
                type="monotone"
                dataKey={c.id}
                name={c.label}
                stroke={c.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
